declare global {
	interface Window {
		Cashfree?: (options?: Record<string, unknown>) => {
			checkout?: (
				options: Record<string, unknown>,
			) => Promise<Record<string, unknown>>;
		};
	}
}

const CASHFREE_SDK_SRC = "https://sdk.cashfree.com/js/v3/cashfree.js";

let sdkLoadPromise: Promise<void> | null = null;

export interface CashfreeCheckoutResult {
	status: "completed" | "cancelled";
	rawResult: Record<string, unknown>;
}

function getCashfreeCheckout() {
	const cashfree = window.Cashfree;

	if (typeof cashfree !== "function") {
		throw new Error("Cashfree SDK is not available");
	}

	const instance = cashfree({ mode: cashfreeMode() });
	if (typeof instance.checkout === "function") {
		return instance.checkout.bind(instance);
	}

	throw new Error("Cashfree checkout is not available");
}

/**
 * The Cashfree environment this build opens checkouts for, read at build time.
 * Unset means `sandbox`, the environment every non-production setup uses. A
 * session opens only in the checkout of the environment that created it, so this
 * has to name the same one the backend's `CASHFREE_API_BASE_URL` does.
 */
export function cashfreeMode(): "sandbox" | "production" {
	return import.meta.env.VITE_CASHFREE_MODE || "sandbox";
}

/**
 * The message to show when the backend minted the funding session in a
 * different Cashfree environment than this build opens checkouts for, or
 * `null` when they agree (or the backend named no environment, as the stub
 * provider does).
 *
 * Opening the checkout anyway fails for every homeowner, at checkout, with
 * nothing that says why, so the caller refuses here instead -- before the SDK
 * loads. Exported and pure so the decision is testable without a browser.
 */
export function cashfreeEnvironmentMismatch(
	sessionEnvironment: "sandbox" | "production" | undefined,
): string | null {
	if (!sessionEnvironment) return null;
	const mode = cashfreeMode();
	if (sessionEnvironment === mode) return null;
	return `Cashfree is misconfigured: this build opens the ${mode} checkout, but the server created the payment in the ${sessionEnvironment} environment. No payment can be taken until the frontend build and the backend credentials name the same Cashfree environment.`;
}

export async function loadCashfreeSdk(): Promise<void> {
	if (window.Cashfree) return;
	if (sdkLoadPromise) {
		await sdkLoadPromise;
		return;
	}

	sdkLoadPromise = new Promise((resolve, reject) => {
		const existingScript = document.querySelector<HTMLScriptElement>(
			`script[src="${CASHFREE_SDK_SRC}"]`,
		);

		if (existingScript) {
			existingScript.addEventListener("load", () => resolve(), { once: true });
			existingScript.addEventListener(
				"error",
				() => reject(new Error("Failed to load Cashfree SDK")),
				{ once: true },
			);
			return;
		}

		const script = document.createElement("script");
		script.src = CASHFREE_SDK_SRC;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
		document.body.appendChild(script);
	});

	await sdkLoadPromise;
}

function checkoutModalVisible(): boolean {
	// The drop-in renders its payment UI in a srcless iframe. Cashfree's
	// own tracking iframe always carries an sdk.cashfree.com src, so an
	// iframe with no src attribute is the checkout modal.
	return Boolean(document.querySelector('iframe:not([src]), iframe[src=""]'));
}

export async function launchCashfreeCheckout(
	paymentSessionId: string,
): Promise<CashfreeCheckoutResult> {
	await loadCashfreeSdk();
	const checkout = getCashfreeCheckout();

	return new Promise<CashfreeCheckoutResult>((resolve) => {
		let settled = false;
		let sawModal = false;

		const finish = (result: CashfreeCheckoutResult) => {
			if (settled) return;
			settled = true;
			window.clearInterval(watcher);
			resolve(result);
		};

		// The SDK promise settles on payment completion and on explicit
		// close, but live sandbox runs showed exits (the in-modal Back
		// button) where it neither resolves nor rejects — the funding
		// banner stayed stuck on "Funding…" until reload. Watch for the
		// modal iframe leaving the DOM and settle as cancelled then.
		const watcher = window.setInterval(() => {
			if (checkoutModalVisible()) {
				sawModal = true;
				return;
			}
			if (sawModal) {
				finish({
					status: "cancelled",
					rawResult: { error: "checkout closed by user" },
				});
			}
		}, 500);

		checkout({
			paymentSessionId,
			redirectTarget: "_modal",
		})
			.then((rawResult) => {
				finish({
					status: rawResult.error ? "cancelled" : "completed",
					rawResult,
				});
			})
			.catch((error: unknown) => {
				finish({
					status: "cancelled",
					rawResult: (error as Record<string, unknown>) ?? {},
				});
			});
	});
}

export function resetCashfreeSdkForTests(): void {
	sdkLoadPromise = null;
}
